<template>
  <div class="flex items-center space-x-1">
    <span v-tooltipable class="truncate text-label">
      {{ fullName }}
    </span>
    <span v-if="note.created_at" class="separator">•</span>
    <span v-if="note.created_at" class="text-label">
      {{ formatDate(note.created_at) }}
    </span>
    <seen-note-button
      :seen-by="note.read_notes || []"
      class="seen-note-button"
      data-testid="seen-notes-button"
      :teleport="shouldTeleport"
    />
    <icon-base
      v-if="buttonIcon"
      v-tooltip="tooltipText"
      height="12"
      width="12"
      view-box="0 0 16 16"
      :icon="buttonIcon"
      icon-color="gray"
      class="-mt-2"
      :data-testid="buttonIcon"
    />

    <icon-base
      v-if="!isApplicationNote && showVisibilityIcon"
      height="20"
      width="20"
      view-box="0 0 20 14"
      icon="show-pass-no-bg"
      class="-mt-1 text-gray-500"
      data-testid="visibility-icon"
    />
    <icon-base
      v-if="isApplicationNote && note.visible_in_deal_list"
      v-tooltip="$t('DEALS.DEAL_NOTES.VISIBLE_IN_DEALS_LIST')"
      data-cy="visible-in-deals-list"
      :icon="IconListView"
      view-box="0 0 24 24"
      class="-mt-1-5 text-gray-500"
    />
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { DealActivityState, FullNotableType } from "@/enums/dealActivity";
import {
  ALL_NOTE_VISIBILITY_TYPES,
  NOTE_VISIBILITY_SCOPES,
} from "@/helpers/constants";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { formatDate, formatUserFullName } from "@/helpers/formatting";
import { useLocalStorageSetting } from "@/hooks/options";

import IconListView from "@/components/icons/IconListView.vue";
import IconPeople from "@/components/icons/IconPeople.vue";
import IconStar from "@/components/icons/IconStar.vue";
import IconUser from "@/components/icons/IconUser.vue";
import IconBase from "@/components/ui/IconBase.vue";
import SeenNoteButton from "@/views/deals/components/dealNotes/SeenNoteButton.vue";

import type { primaryVisibilityScope } from "@/models/common";
import type { NoteLogData } from "@/models/communicationLogs";
import type { Component } from "vue";

const { note } = defineProps<{ note: NoteLogData }>();

const VISIBILITY_SCOPES_WITH_BUTTONS = [1, 3];
const OTHER_VISIBILITY_SCOPE_INDEX = 99;

const iconMap: Record<string, Record<string, Component>> = {
  people: IconPeople,
  user: IconUser,
  star: IconStar,
};

const { t } = useI18n();

const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);

const shouldTeleport = computed(
  () =>
    ![DealActivityState.Draggable, DealActivityState.PiP].includes(
      dealActivityOptions.value.state
    )
);

const visibilityIconIndex = computed<primaryVisibilityScope>(() => {
  if (VISIBILITY_SCOPES_WITH_BUTTONS.includes(note.visibility_scope)) {
    return note.visibility_scope as primaryVisibilityScope;
  }
  return OTHER_VISIBILITY_SCOPE_INDEX;
});

const buttonIcon = computed(() => {
  const iconName = NOTE_VISIBILITY_SCOPES[visibilityIconIndex.value]?.icon;
  return iconMap[iconName] ?? null;
});

const tooltipText = computed(() => {
  const visibilityType = ALL_NOTE_VISIBILITY_TYPES[note.visibility_scope];
  return visibilityType
    ? `${t("COMMON.VISIBILITY").toLowerCase()}: ${visibilityType}`
    : "";
});

const isApplicationNote = computed(
  () => note.notable_type === FullNotableType.application
);

const showVisibilityIcon = computed(() => {
  return (
    note.notable_type === FullNotableType.application &&
    visibilityIconIndex.value !== OTHER_VISIBILITY_SCOPE_INDEX
  );
});

const fullName = computed(() => {
  return formatUserFullName(note.user);
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

@container (max-width: 300px) {
  .note-footer {
    @apply flex-col flex-wrap items-start space-y-1;
    .space-x-2 {
      @apply shrink space-x-3;
    }
    .seen-note-button {
      @apply ml-0;
    }
  }
  .note-footer .separator {
    display: none;
  }
}
</style>
